/**
 * Lazy loader for the branded date / time pickers (flatpickr + timepicker-ui).
 *
 * The including template renders one <script defer src="...lazy-datetime-pickers.js">
 * carrying the asset URLs as data attributes:
 *   data-css   JSON array of stylesheet URLs, appended to <head> in order
 *   data-js    JSON array of script URLs, loaded one after another so flatpickr and
 *              timepicker-ui exist before datetime-pickers.js runs
 * (components/form/datetime_picker_lazy.html builds them through {% static %}.)
 *
 * Nothing is fetched until the first of these happens:
 *   - the quote form container comes within 800px of the viewport
 *     (IntersectionObserver; falls back to observing every date/time input when
 *     the container is missing);
 *   - focusin or pointerdown on a date/time input or anywhere inside the quote form;
 *   - without IntersectionObserver: the first scroll, pointerdown, keydown or
 *     touchstart.
 *
 * Late loading is safe: the inputs are native <input type="date|time"> until
 * datetime-pickers.js upgrades them, and that script also upgrades inputs added later.
 * A load failure goes to window.reportClientError when the website error reporter is
 * present. Safe to include more than once: the second copy is a no-op.
 */
(function () {
    'use strict';

    if (window.__lazyDatetimePickersInit) return;
    window.__lazyDatetimePickersInit = true;

    var script = document.currentScript;
    if (!script) return;

    var ROOT_MARGIN = '800px 0px';
    var INTERACTION_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'];
    var FORM_SELECTOR = '#service-quote-section, #sq-form';
    var INPUT_SELECTOR = 'input[type="date"], input[type="time"]';

    function parseList(attr) {
        try {
            var list = JSON.parse(script.getAttribute(attr) || '[]');
            return Array.isArray(list) ? list : [];
        } catch (e) {
            return [];
        }
    }

    var cssUrls = parseList('data-css');
    var jsUrls = parseList('data-js');

    function report(err, src) {
        try {
            if (typeof window.reportClientError === 'function') {
                window.reportClientError(err, { source: 'lazy-datetime-pickers', context: { src: src } });
            }
        } catch (e) { /* reporting must never throw */ }
    }

    var loaded = false;
    var teardown = [];

    // Focus tracking for the load window. The last script upgrades the inputs, which
    // hides a focused native one and drops focus to <body>. Chrome reports the hidden
    // input as document.activeElement until the next focus fix-up (after the script's
    // load event); other engines blur at once. So: remember the last focused date/time
    // input, and only hand focus back when the upgrade, not the user, took it away.
    var track = { el: null, upgradeBlur: false };
    var lastScript = null; // the last <script> appended: the one that runs the upgrade

    // True while the upgrade script itself is executing (an engine that blurs a hidden input
    // synchronously fires focusout from inside it). Falls back to its src if the element
    // reference is not the one reported.
    function inUpgradeScript() {
        var cs = document.currentScript;
        if (!cs || !lastScript) return false;
        return cs === lastScript || (!!cs.getAttribute && cs.getAttribute('src') === lastScript.getAttribute('src'));
    }

    function onTrackIn(event) {
        var t = event.target;
        if (t && typeof t.matches === 'function' && t.matches(INPUT_SELECTOR)) {
            track.el = t;
            track.upgradeBlur = false;
        }
    }

    function onTrackOut(event) {
        if (event.target !== track.el) return;
        // Moving to another element is the user's choice. A blur to nowhere counts as the
        // upgrade's only if it fired while the upgrade script was running.
        if (event.relatedTarget) track.el = null;
        else track.upgradeBlur = inUpgradeScript();
    }

    function startTracking() {
        var active = document.activeElement;
        if (active && typeof active.matches === 'function' && active.matches(INPUT_SELECTOR)) {
            track.el = active;
        }
        document.addEventListener('focusin', onTrackIn, true);
        document.addEventListener('focusout', onTrackOut, true);
    }

    function stopTracking() {
        document.removeEventListener('focusin', onTrackIn, true);
        document.removeEventListener('focusout', onTrackOut, true);
    }

    // True when focus was on `prev` right up to the upgrade and the upgrade took it.
    function lostToUpgrade(prev, upgradeBlur) {
        var active = document.activeElement;
        if (active === prev) return true; // hidden but still reported as active (Chrome)
        if (active && active !== document.body && active !== document.documentElement) return false;
        // On <body>: only a blur fired from inside the upgrade script counts; one the user made
        // earlier stays where it is.
        return upgradeBlur && (prev.type === 'hidden' || prev.hidden);
    }

    function loadScripts(index) {
        if (index >= jsUrls.length) return;
        var url = jsUrls[index];
        var el = document.createElement('script');
        el.src = url;
        el.async = false;
        var last = index === jsUrls.length - 1;
        // Chain on load/error so each script runs only after the previous one finished.
        el.onload = function () {
            if (last) {
                // The upgrade ran synchronously inside this script, so the state is read now.
                var prev = track.el;
                var dropped = !!prev && lostToUpgrade(prev, track.upgradeBlur);
                stopTracking();
                if (dropped) restoreFocus(prev);
            }
            loadScripts(index + 1);
        };
        el.onerror = function () {
            report(new Error('Date/time picker script failed to load: ' + url), url);
            if (last) stopTracking();
            // Keep going: the native inputs keep working if a later script is missing.
            loadScripts(index + 1);
        };
        if (last) lastScript = el;
        document.head.appendChild(el);
    }

    // Put focus back on the visible replacement of an input that lost it in the upgrade.
    // Never opens the calendar or time dial, and leaves focus alone if the user moved on.
    function restoreFocus(prev) {
        requestAnimationFrame(function () {
            var current = document.activeElement;
            if (current && current !== prev && current !== document.body && current !== document.documentElement) return;
            var target = window.visibleDateTimeInput ? window.visibleDateTimeInput(prev) : prev;
            if (!target || !target.isConnected || target.type === 'hidden' || target.hidden) return;
            // flatpickr opens its calendar on the alt input's focus event (clickOpens); take that
            // one listener off for this focus() call so nothing opens, then put it back.
            var fp = prev._flatpickr;
            var guarded = !!fp && fp._input === target && typeof fp.open === 'function';
            if (guarded) target.removeEventListener('focus', fp.open);
            try {
                target.focus({ preventScroll: true });
            } catch (e) { /* focus is best effort */ } finally {
                if (guarded) target.addEventListener('focus', fp.open);
            }
        });
    }

    function load() {
        if (loaded) return;
        loaded = true;
        teardown.forEach(function (fn) { fn(); });
        teardown = [];
        if (jsUrls.length) startTracking();

        cssUrls.forEach(function (url) {
            var link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = url;
            link.onerror = function () {
                report(new Error('Date/time picker stylesheet failed to load: ' + url), url);
            };
            document.head.appendChild(link);
        });
        loadScripts(0);
    }

    function inFormOrInput(target) {
        if (!target || typeof target.closest !== 'function') return false;
        return !!target.closest(INPUT_SELECTOR + ', ' + FORM_SELECTOR);
    }

    function onFocusOrPointer(event) {
        if (inFormOrInput(event.target)) load();
    }

    function init() {
        if (!cssUrls.length && !jsUrls.length) return;

        document.addEventListener('focusin', onFocusOrPointer, true);
        document.addEventListener('pointerdown', onFocusOrPointer, true);
        teardown.push(function () {
            document.removeEventListener('focusin', onFocusOrPointer, true);
            document.removeEventListener('pointerdown', onFocusOrPointer, true);
        });

        if ('IntersectionObserver' in window) {
            var targets = Array.prototype.slice.call(document.querySelectorAll(FORM_SELECTOR));
            // The date/time inputs sit in a hidden step of the form; they are only a
            // fallback for a page where the form container is missing.
            if (!targets.length) {
                targets = Array.prototype.slice.call(document.querySelectorAll(INPUT_SELECTOR));
            }
            if (targets.length) {
                var observer = new IntersectionObserver(function (entries) {
                    for (var i = 0; i < entries.length; i++) {
                        if (entries[i].isIntersecting) { load(); return; }
                    }
                }, { rootMargin: ROOT_MARGIN });
                targets.forEach(function (el) { observer.observe(el); });
                teardown.push(function () { observer.disconnect(); });
                return;
            }
        }

        // No IntersectionObserver (or nothing to observe): first interaction loads it.
        function onInteraction() { load(); }
        INTERACTION_EVENTS.forEach(function (name) {
            window.addEventListener(name, onInteraction, { once: true, passive: true });
        });
        teardown.push(function () {
            INTERACTION_EVENTS.forEach(function (name) {
                window.removeEventListener(name, onInteraction);
            });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
